
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Mic, MicOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

interface VoiceInputProps {
  onTranscript: (text: string) => void;
}

const VoiceInput: React.FC<VoiceInputProps> = ({ onTranscript }) => {
  const [isListening, setIsListening] = useState(false);
  const [recognition, setRecognition] = useState<any>(null);
  const [isSupported, setIsSupported] = useState(true);
  const { language, t } = useLanguage();

  // Initialize speech recognition
  useEffect(() => {
    // Clean up previous recognition instance if it exists
    if (recognition && isListening) {
      try {
        recognition.stop();
        setIsListening(false);
      } catch (error) {
        console.error('Error stopping recognition during cleanup:', error);
      }
    }

    // Check if browser supports speech recognition
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      try {
        // Create speech recognition instance
        const SpeechRecognition = window.webkitSpeechRecognition || window.SpeechRecognition;
        const recognitionInstance = new SpeechRecognition();
        
        // Configure
        recognitionInstance.continuous = true;
        recognitionInstance.interimResults = true;
        recognitionInstance.lang = language === 'ur' ? 'ur-PK' : 'en-US';
        
        // Set up event handlers
        recognitionInstance.onresult = (event: any) => {
          let currentTranscript = '';
          for (let i = 0; i < event.results.length; i++) {
            currentTranscript += event.results[i][0].transcript;
          }
          onTranscript(currentTranscript);
        };
        
        recognitionInstance.onerror = (event: any) => {
          console.error('Speech recognition error', event);
          if (isListening) {
            setIsListening(false);
            toast.error(language === 'ur' ? 'آواز کی شناخت میں خرابی' : 'Voice recognition error');
          }
        };
        
        recognitionInstance.onend = () => {
          setIsListening(false);
        };
        
        setRecognition(recognitionInstance);
      } catch (error) {
        console.error('Error initializing speech recognition:', error);
        setIsSupported(false);
      }
    } else {
      setIsSupported(false);
      toast.error(language === 'ur' ? 'آپ کا براؤزر آواز کی شناخت کی حمایت نہیں کرتا' : 'Your browser does not support voice recognition');
    }
    
    // Clean up function
    return () => {
      if (recognition && isListening) {
        try {
          recognition.stop();
        } catch (error) {
          console.error('Error stopping recognition during cleanup:', error);
        }
      }
    };
  }, [language]); // Recreate recognition when language changes

  const toggleListening = () => {
    if (!recognition) return;
    
    try {
      if (isListening) {
        recognition.stop();
        setIsListening(false);
      } else {
        // Reset any previous state
        recognition.abort();
        setTimeout(() => {
          try {
            recognition.start();
            setIsListening(true);
            toast.info(language === 'ur' ? 'آپ بول سکتے ہیں...' : 'You can speak now...');
          } catch (error) {
            console.error('Error starting recognition:', error);
            setIsListening(false);
            toast.error(language === 'ur' ? 'آواز کی شناخت شروع کرنے میں خرابی' : 'Error starting voice recognition');
          }
        }, 100);
      }
    } catch (error) {
      console.error('Error in toggle listening:', error);
      setIsListening(false);
    }
  };

  if (!isSupported) {
    return null;
  }

  return (
    <div className="flex items-center justify-center mt-2">
      <Button
        type="button"
        variant={isListening ? "destructive" : "secondary"}
        size="sm"
        className={`flex items-center gap-2 transition-all duration-300 ${isListening ? 'animate-pulse' : ''}`}
        onClick={toggleListening}
        disabled={!recognition}
      >
        {isListening ? (
          <>
            <MicOff size={16} className={language === 'ur' ? 'ml-2' : 'mr-2'} />
            {language === 'ur' ? 'روکیں' : 'Stop'}
          </>
        ) : (
          <>
            {recognition ? (
              <>
                <Mic size={16} className={language === 'ur' ? 'ml-2' : 'mr-2'} />
                {language === 'ur' ? 'بولیں' : 'Speak'}
              </>
            ) : (
              <>
                <Loader2 className="animate-spin" size={16} />
                {language === 'ur' ? 'لوڈ ہو رہا ہے' : 'Loading'}
              </>
            )}
          </>
        )}
      </Button>
    </div>
  );
};

export default VoiceInput;
